import type {
  Tournoi,
  TournoiHomeContent,
  TournoiHomeHighlight,
} from "../types/Tournoi";
import {
  GALLERY_IMAGES,
  HOME_EVENT_DOTATIONS_LINE,
  HOME_EVENT_WEEKEND_LINE,
  HOME_REGISTRATION_OPEN_TIME_LABEL,
  HOME_SPECTATOR_TABLES_LINE,
  HOME_WHY_PARTICIPATE_LEVELS,
  HOME_WHY_PARTICIPATE_ORGANIZATION,
} from "../components/Home/homeConstants";

const DEFAULT_POSTER_URL = "/2026.png";
const DEFAULT_POSTER_ALT =
  "Affiche du tournoi national SQY Ping 2026 — dates, lieu, dotations et grilles horaires";

export type ResolvedTournoiHome = Required<
  Pick<
    TournoiHomeContent,
    | "weekendLine"
    | "dotationsLine"
    | "posterAlt"
    | "galleryUrls"
    | "registrationOpenTimeLabel"
    | "whyParticipateOrganization"
    | "whyParticipateLevels"
    | "spectatorTablesLine"
  >
> & {
  /** URL affiche ; `null` = ne pas afficher (pas de fallback). */
  posterUrl: string | null;
  hidePoster: boolean;
  /**
   * Points marketing optionnels. Tableau vide = ne pas afficher la section
   * (comportement site A / tournois sans highlights).
   */
  highlights: TournoiHomeHighlight[];
  highlightsEyebrow: string;
  highlightsTitle: string;
  highlightsSubtitle: string;
};

function nonEmpty(value: string | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function nonEmptyList(values: string[] | undefined): string[] | undefined {
  if (!Array.isArray(values) || values.length === 0) return undefined;
  const cleaned = values.map((v) => v.trim()).filter((v) => v.length > 0);
  return cleaned.length > 0 ? cleaned : undefined;
}

function normalizeHighlights(
  values: TournoiHomeHighlight[] | undefined
): TournoiHomeHighlight[] {
  if (!Array.isArray(values) || values.length === 0) return [];
  return values
    .map((item) => {
      const title = nonEmpty(item?.title);
      const body = nonEmpty(item?.body);
      if (!title || !body) return null;
      const icon = nonEmpty(item?.icon);
      return icon ? { title, body, icon } : { title, body };
    })
    .filter((item): item is TournoiHomeHighlight => item !== null);
}

/**
 * Fusionne `tournoi.home` avec les fallbacks historiques hardcodés.
 * Les champs absents / vides conservent le comportement actuel du site A.
 * `home.hidePoster === true` : pas d’affiche et pas de fallback (scoped par tournoi).
 */
export function resolveTournoiHome(
  tournoi: Tournoi | null | undefined
): ResolvedTournoiHome {
  const home = tournoi?.home;
  const hidePoster = home?.hidePoster === true;
  const configuredPoster = nonEmpty(home?.posterUrl);
  return {
    weekendLine:
      nonEmpty(home?.weekendLine) ?? HOME_EVENT_WEEKEND_LINE,
    dotationsLine:
      nonEmpty(home?.dotationsLine) ?? HOME_EVENT_DOTATIONS_LINE,
    hidePoster,
    posterUrl: hidePoster
      ? null
      : configuredPoster ?? DEFAULT_POSTER_URL,
    posterAlt: nonEmpty(home?.posterAlt) ?? DEFAULT_POSTER_ALT,
    galleryUrls: nonEmptyList(home?.galleryUrls) ?? [...GALLERY_IMAGES],
    registrationOpenTimeLabel:
      nonEmpty(home?.registrationOpenTimeLabel) ??
      HOME_REGISTRATION_OPEN_TIME_LABEL,
    whyParticipateOrganization:
      nonEmpty(home?.whyParticipateOrganization) ??
      HOME_WHY_PARTICIPATE_ORGANIZATION,
    whyParticipateLevels:
      nonEmpty(home?.whyParticipateLevels) ?? HOME_WHY_PARTICIPATE_LEVELS,
    spectatorTablesLine:
      nonEmpty(home?.spectatorTablesLine) ?? HOME_SPECTATOR_TABLES_LINE,
    highlights: normalizeHighlights(home?.highlights),
    highlightsEyebrow:
      nonEmpty(home?.highlightsEyebrow) ?? "Temps forts",
    highlightsTitle:
      nonEmpty(home?.highlightsTitle) ?? "Ce qui rend cette journée unique",
    highlightsSubtitle:
      nonEmpty(home?.highlightsSubtitle) ??
      "Des atouts propres à cette édition — au-delà des tableaux et des dotations.",
  };
}
